/**
 * MacroList Component
 * Displays and manages the list of created macros
 */

import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Trash2, ListChecks, AlertTriangle } from 'lucide-react'
import { Macro, MacroPattern } from '../../types/macros'
import { generateMacroSyntax, extractKeysFromSyntax } from '../../utils/macroHelpers'
import EmptyStateIllustration from '../ui/EmptyStateIllustration'

interface MacroListProps {
  macros: Macro[]
  onDeleteMacro: (id: string) => void
  onClearAll: () => void
}

const MacroList: React.FC<MacroListProps> = ({ macros, onDeleteMacro, onClearAll }) => {
  const getPatternBadgeColor = (pattern: MacroPattern): string => {
    switch (pattern) {
      case MacroPattern.SIMPLE:
        return 'bg-blue-100 text-blue-700 border-blue-200'
      case MacroPattern.FALLBACK:
        return 'bg-green-100 text-green-700 border-green-200'
      case MacroPattern.CONDITIONAL:
        return 'bg-purple-100 text-purple-700 border-purple-200'
      case MacroPattern.CONDITIONAL_ELSE:
        return 'bg-orange-100 text-orange-700 border-orange-200'
      case MacroPattern.ADVANCED:
        return 'bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 border-purple-300'
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200'
    }
  }

  const getPatternLabel = (pattern: MacroPattern): string => {
    switch (pattern) {
      case MacroPattern.SIMPLE:
        return 'Simple'
      case MacroPattern.FALLBACK:
        return 'Fallback'
      case MacroPattern.CONDITIONAL:
        return 'Conditional'
      case MacroPattern.CONDITIONAL_ELSE:
        return 'Conditional + Else'
      case MacroPattern.ADVANCED:
        return 'Advanced/Combined'
      default:
        return 'Unknown'
    }
  }

  const handleClearAllWithConfirmation = () => {
    if (window.confirm('Are you sure you want to clear all macros? This action cannot be undone.')) {
      onClearAll()
    }
  }

  if (macros.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-[#1A2B49]">Macro List</h3>
          <ListChecks className="w-5 h-5 text-[#6E6D7A]" />
        </div>
        <div className="py-8">
          <EmptyStateIllustration
            type="body"
            title="No Macros Yet"
            description="Add a macro using the builder above or load a template to get started"
          />
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <h3 className="text-xl font-bold text-[#1A2B49]">Macro List</h3>
          <span className="bg-[#FE4215] text-white text-xs font-semibold px-2.5 py-1 rounded-full">
            {macros.length}
          </span>
        </div>
        <button
          onClick={handleClearAllWithConfirmation}
          className="flex items-center space-x-1.5 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
          title="Clear all macros"
        >
          <AlertTriangle className="w-4 h-4" />
          <span>Clear All</span>
        </button>
      </div>

      <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
        <AnimatePresence mode="popLayout">
          {macros.map((macro, index) => (
            <motion.div
              key={macro.id}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-white"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  {/* Pattern Badge and Number */}
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-xs font-semibold text-gray-500">#{index + 1}</span>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getPatternBadgeColor(
                        macro.pattern
                      )}`}
                    >
                      {getPatternLabel(macro.pattern)}
                    </span>
                  </div>

                  {/* Macro Key or Detected Keys */}
                  {macro.pattern === MacroPattern.ADVANCED ? (
                    <div className="mb-2">
                      <span className="text-sm font-medium text-[#1A2B49]">Detected Keys:</span>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {extractKeysFromSyntax(macro.customSyntax || '').map(key => (
                          <span
                            key={key}
                            className="inline-flex items-center px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full border border-green-200"
                          >
                            {key}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="mb-2">
                      <span className="text-sm font-medium text-[#1A2B49]">Key:</span>
                      <code className="ml-2 text-sm bg-gray-100 px-2 py-1 rounded text-[#233D71] font-mono">
                        {macro.key}
                      </code>
                    </div>
                  )}

                  {/* Generated Syntax */}
                  <div className="mb-2">
                    <span className="text-sm font-medium text-[#1A2B49]">Syntax:</span>
                    <div className="mt-1 p-2 bg-gray-50 rounded border border-gray-200">
                      <code className="text-xs text-gray-700 break-all font-mono">
                        {generateMacroSyntax(macro)}
                      </code>
                    </div>
                  </div>

                  {/* Description */}
                  {macro.description && (
                    <div>
                      <span className="text-sm font-medium text-[#1A2B49]">Description:</span>
                      <p className="text-xs text-[#6E6D7A] mt-1">{macro.description}</p>
                    </div>
                  )}

                  {/* Additional Pattern Details */}
                  {macro.pattern === MacroPattern.FALLBACK && macro.fallbackValue && (
                    <div className="mt-2 text-xs">
                      <span className="text-[#6E6D7A]">Fallback:</span>
                      <span className="ml-2 text-[#1A2B49] font-medium">"{macro.fallbackValue}"</span>
                    </div>
                  )}

                  {(macro.pattern === MacroPattern.CONDITIONAL ||
                    macro.pattern === MacroPattern.CONDITIONAL_ELSE) &&
                    macro.conditionalContent && (
                      <div className="mt-2 text-xs">
                        <span className="text-[#6E6D7A]">When present:</span>
                        <p className="ml-2 text-[#1A2B49] font-medium truncate">
                          {macro.conditionalContent}
                        </p>
                      </div>
                    )}

                  {macro.pattern === MacroPattern.CONDITIONAL_ELSE && macro.elseContent && (
                    <div className="mt-2 text-xs">
                      <span className="text-[#6E6D7A]">When missing:</span>
                      <p className="ml-2 text-[#1A2B49] font-medium truncate">
                        {macro.elseContent}
                      </p>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col space-y-2 ml-4">
                  <button
                    onClick={() => onDeleteMacro(macro.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    title="Delete macro"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default MacroList
